import { useApp } from '@/contexts';

import { useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { DeleteConfirmDialog, PageLoader } from '@/components/common';
import { PageContainer, PageHeading } from '@/components/layout';
import { useGetTenantQuery } from '@/features/tenants/api/tenantsApi';
import { logger } from '@/utils/logger';

import { useDeleteObjectTypeMutation, useGetAllObjectTypesQuery } from '../api/eventsApi';
import type { ObjectType } from '../types';

// Move ObjectListItem outside to avoid re-creation on render
interface ObjectListItemProps {
  obj: ObjectType;
  sys?: boolean;
  identityIsAdmin: boolean;
  onEdit: (obj: ObjectType) => void;
  onDelete: (obj: ObjectType) => void;
}

const ObjectListItem = ({
  obj,
  sys = false,
  identityIsAdmin,
  onEdit,
  onDelete,
}: ObjectListItemProps) => {
  const canEdit = !sys && identityIsAdmin;

  return (
    <ListItem
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: '4px',
        mb: 1,
        px: 2,
        py: 1,
        '&:hover': {
          bgcolor: canEdit ? 'grey.50' : 'background.paper',
        },
      }}
      secondaryAction={
        canEdit ? (
          <>
            <IconButton edge="end" size="small" onClick={() => onEdit(obj)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton edge="end" size="small" onClick={() => onDelete(obj)}>
              <DeleteIcon fontSize="small" color="error" />
            </IconButton>
          </>
        ) : null
      }
    >
      <ListItemText
        primary={obj.name}
        slotProps={{
          primary: {
            sx: { fontSize: '13px' },
          },
          secondary: {
            sx: { fontSize: '11px' },
          },
        }}
        secondary={
          obj.private && (obj.createdAt || obj.createdBy) ? (
            <>
              {obj.createdAt && obj.createdBy
                ? `${obj.createdAt} | ${obj.createdBy}`
                : obj.createdAt || obj.createdBy}
            </>
          ) : (
            ''
          )
        }
      />
    </ListItem>
  );
};

export const EventsObjectsPage = () => {
  const { appId, tenantId, app } = useApp();
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<ObjectType | null>(null);

  const { data: tenant } = useGetTenantQuery(tenantId, {
    skip: !tenantId,
  });

  const { data: objectTypes, isLoading } = useGetAllObjectTypesQuery(
    {
      tenantKey: tenant?.key || '',
      appKey: app.uniqueKey || '',
    },
    { skip: !tenant?.key || !app.uniqueKey }
  );

  const [deleteObjectType] = useDeleteObjectTypeMutation();

  // TODO: Get identityIsAdmin from app response (not in type definition yet)
  const identityIsAdmin = (app as any)?.identityIsAdmin ?? false; // eslint-disable-line @typescript-eslint/no-explicit-any -- App type incomplete

  const handleEdit = (obj: ObjectType) => {
    // Only allow editing private objects (Event REST API Objects)
    if (obj.private && identityIsAdmin) {
      navigate(`/a/${appId}/events/obj/${obj.name}/edit`);
    }
  };

  const handleDelete = async () => {
    if (!selectedForDelete || !tenant?.key || !app.uniqueKey) {
      setDeleteModal(false);
      setSelectedForDelete(null);
      return;
    }

    try {
      await deleteObjectType({
        tenantKey: tenant.key,
        appKey: app.uniqueKey,
        name: selectedForDelete.name,
      }).unwrap();
    } catch (error) {
      logger.error('Failed to delete object type:', error);
    } finally {
      setDeleteModal(false);
      setSelectedForDelete(null);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageLoader />
      </PageContainer>
    );
  }

  // Categorize object types
  const eventRestApiObjects: ObjectType[] = [];
  const brickFTPObjects: ObjectType[] = [];
  const systemObjects: ObjectType[] = [];
  const dedupObjects: ObjectType[] = [];

  // Add File Upload Event (BrickFTP)
  brickFTPObjects.push({
    name: 'File Upload Event',
    private: false,
  });

  // Safely iterate over objectTypes (handle null/undefined/non-array)
  if (Array.isArray(objectTypes)) {
    objectTypes.forEach((obj) => {
      if (!obj.private) {
        systemObjects.push(obj);
      } else if (obj.name.startsWith('dedup.')) {
        dedupObjects.push(obj);
      } else {
        eventRestApiObjects.push(obj);
      }
    });
  }

  const handleDeleteForObject = (obj: ObjectType) => {
    setSelectedForDelete(obj);
    setDeleteModal(true);
  };

  return (
    <PageContainer>
      <PageHeading
        title="Manage Objects"
        subtitle="Configure event object types and REST API objects"
        action={
          <Button
            variant="contained"
            size="small"
            disabled={!identityIsAdmin}
            onClick={() => navigate(`/a/${appId}/events/obj/new`)}
          >
            New Object
          </Button>
        }
      />

      {/* Event REST API Objects */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontSize: '16px', fontWeight: 400 }}>
          Event REST API Objects
        </Typography>
        {eventRestApiObjects.length > 0 ? (
          <List sx={{ p: 0 }}>
            {eventRestApiObjects.map((obj) => (
              <ObjectListItem
                key={`api-${obj.name}`}
                obj={obj}
                identityIsAdmin={identityIsAdmin}
                onEdit={handleEdit}
                onDelete={handleDeleteForObject}
              />
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 3, fontSize: '12px' }}>
            None
          </Typography>
        )}
      </Box>

      {/* BrickFTP Objects */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontSize: '16px', fontWeight: 400 }}>
          BrickFTP Objects
        </Typography>
        <List sx={{ p: 0 }}>
          {brickFTPObjects.map((obj) => (
            <ObjectListItem
              key={`ftp-${obj.name}`}
              obj={obj}
              sys
              identityIsAdmin={identityIsAdmin}
              onEdit={handleEdit}
              onDelete={handleDeleteForObject}
            />
          ))}
        </List>
      </Box>

      {/* System Objects */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontSize: '16px', fontWeight: 400 }}>
          System Objects
        </Typography>
        {systemObjects.length > 0 ? (
          <List sx={{ p: 0 }}>
            {systemObjects.map((obj) => (
              <ObjectListItem
                key={`sys-${obj.name}`}
                obj={obj}
                sys
                identityIsAdmin={identityIsAdmin}
                onEdit={handleEdit}
                onDelete={handleDeleteForObject}
              />
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 3, fontSize: '12px' }}>
            None
          </Typography>
        )}
      </Box>

      {/* Dedup Objects */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontSize: '16px', fontWeight: 400 }}>
          Dedup Objects
        </Typography>
        {dedupObjects.length > 0 ? (
          <List sx={{ p: 0 }}>
            {dedupObjects.map((obj) => (
              <ObjectListItem
                key={`dedup-${obj.name}`}
                obj={obj}
                sys
                identityIsAdmin={identityIsAdmin}
                onEdit={handleEdit}
                onDelete={handleDeleteForObject}
              />
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 3, fontSize: '12px' }}>
            None
          </Typography>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteModal}
        onClose={() => {
          setDeleteModal(false);
          setSelectedForDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Object"
        itemName={selectedForDelete?.name}
      />
    </PageContainer>
  );
};
